import * as React from 'react';
import { useCallback, useEffect } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { TrashIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';

import { CollaboratorModalFooterContext } from '../collaborator-modal-footer-context';
import { selectedCollaboratorAtom, useCollaboratorActionModal } from '../../../collaborators.atoms';
import useDeleteEmployerUserContact from '../../../hooks/use-delete-collaborator';

import { cn } from '@restworld/utils-common';
export enum RemoveCollaboratorSteps {
  CONFIRMATION = 'CONFIRMATION',
  VIEW_SUCCESS_MESSAGE = 'VIEW_SUCCESS_MESSAGE',
}

function RemoveCollaboratorConfirmationScreen({ setStep }: RemoveCollaboratorConfirmationScreenProps) {
  const { t } = useTranslation('collaborators-page');
  const footerContext = React.useContext(CollaboratorModalFooterContext);
  const { closeCollaboratorActionModal } = useCollaboratorActionModal();
  const [selectedCollaborator] = useAtom(selectedCollaboratorAtom);
  const firstName = selectedCollaborator?.user?.name;
  const userId = selectedCollaborator?.user_id;
  const { handleRemoveCollaborator, isPending } = useRemoveCollaboratorConfirmation({ userId, setStep });

  const footerEl = React.useMemo(
    () => (
      // px-8 py-4 to match the other collaborator-modal footers (ModalContent is noPadding, so the
      // footer must pad itself; without it the CTAs sat flush against the modal edges).
      <div className="flex flex-col gap-5 w-full justify-between md:flex-row px-8 py-4">
        <ButtonV2 variant="tertiary" onClick={closeCollaboratorActionModal}>
          {t('no_go_back')}
        </ButtonV2>
        <ButtonV2
          variant="secondary-danger"
          StartIcon={TrashIcon}
          loading={isPending}
          onClick={handleRemoveCollaborator}
        >
          {t('yes_remove_collaborator')}
        </ButtonV2>
      </div>
    ),
    [closeCollaboratorActionModal, handleRemoveCollaborator, isPending, t]
  );

  React.useEffect(() => {
    if (footerContext) {
      footerContext.setFooter(footerEl);
      return () => footerContext.setFooter(null);
    }
  }, [footerContext, footerEl]);

  return (
    <Wrapper>
      <Heading color="neutral" className="text-lg lg:text-xl">
        {`${t('are_you_sure_you_want_to_remove')} ${firstName} ${t('as_a_collaborator')}?`}
      </Heading>
      <ContentWrapper>
        <Image src="/utils-assets/person-kneeling.svg" alt="" height={320} width={320} className="mx-auto my-8" />
        {!footerContext && (
          <>
            <ButtonV2 variant="tertiary" onClick={closeCollaboratorActionModal} fullwidth className={'mb-6'}>
              {t('no_go_back')}
            </ButtonV2>
            <ButtonV2
              variant="secondary-danger"
              StartIcon={TrashIcon}
              loading={isPending}
              onClick={handleRemoveCollaborator}
              fullwidth
            >
              {t('yes_remove_collaborator')}
            </ButtonV2>
          </>
        )}
      </ContentWrapper>
    </Wrapper>
  );
}
export default RemoveCollaboratorConfirmationScreen;

function useRemoveCollaboratorConfirmation({
  userId,
  setStep,
}: {
  userId: string;
  setStep: (step: RemoveCollaboratorSteps) => void;
}) {
  const { mutate, isSuccess, isPending } = useDeleteEmployerUserContact();

  const handleRemoveCollaborator = useCallback(() => {
    mutate(userId);
  }, [mutate, userId]);

  useEffect(() => {
    if (isSuccess) {
      setStep(RemoveCollaboratorSteps.VIEW_SUCCESS_MESSAGE);
    }
  }, [isSuccess, setStep]);

  return { handleRemoveCollaborator, isPending };
}

interface RemoveCollaboratorConfirmationScreenProps {
  setStep: (step: RemoveCollaboratorSteps) => void;
}

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'h-full w-full max-w-[640px] mx-auto flex flex-col justify-center items-center gap-5 text-center',
      className
    )}
    {...props}
  />
);
const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-neutral-200 font-medium max-w-[576px] lg:min-w-[576px]', className)} {...props} />
);
